
import { Profile } from "@/types/profile";
import { ProfileBio } from "./ProfileBio";
import { RecipeSection } from "./RecipeSection";
import { ProfileReviews } from "./ProfileReviews";
import { SubscriptionCTA } from "./SubscriptionCTA";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

interface ProfileContentProps {
  profile: Profile;
  isSubscribed: boolean;
  isMobile: boolean;
  userLoading: boolean;
  stripeConfig: { publishableKey: string } | undefined;
  handleSubscribe: (type: 'monthly' | 'lifetime') => Promise<any>;
  subscriptionType?: 'monthly' | 'lifetime';
  onCancelSubscription?: () => Promise<void>;
  isCancelled?: boolean;
}

export const ProfileContent = ({
  profile,
  isSubscribed,
  isMobile,
  userLoading,
  stripeConfig,
  handleSubscribe,
  subscriptionType,
  onCancelSubscription,
  isCancelled
}: ProfileContentProps) => {
  // Get current user to check if they're the creator
  const { data: currentUser } = useQuery({
    queryKey: ['current-user'],
    queryFn: async () => {
      const { data: { user }, error } = await supabase.auth.getUser();
      if (error) {
        console.error("Auth error:", error);
        return null;
      }
      return user;
    },
  });

  // Check if current user is the creator of this profile
  const isCreator = currentUser?.id === profile.id;
  
  // Filter recipes by visibility
  const publicRecipes = profile.recipes?.filter(recipe => recipe.visibility === 'public').map(recipe => ({
    ...recipe,
    type: 'recipe' as const
  })) ?? [];
  
  const premiumRecipes = profile.recipes?.filter(recipe => recipe.visibility === 'subscription').map(recipe => ({
    ...recipe,
    type: 'recipe' as const
  })) ?? [];
  
  const allPackages = [
    ...(profile.packages?.filter(pkg => pkg.visibility === 'public').map(pkg => ({
      ...pkg,
      type: 'package' as const
    })) ?? []),
    ...(profile.packages?.filter(pkg => pkg.visibility === 'subscription').map(pkg => ({
      ...pkg,
      type: 'package' as const
    })) ?? [])
  ];

  // Creators always have access to their own content
  const hasAccess = isCreator || isSubscribed;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
      <div className="md:col-span-2 space-y-8">
        <ProfileBio bio={profile.bio} />
        <RecipeSection 
          title="Public Recipes"
          recipes={publicRecipes}
          isSubscribed={hasAccess}
          profileUsername={profile.username}
          isMobile={isMobile}
        />
        <RecipeSection 
          title="Premium Recipes"
          recipes={premiumRecipes}
          isSubscribed={hasAccess}
          profileUsername={profile.username}
          isMobile={isMobile}
        />
        <RecipeSection 
          title="Cookbooks"
          recipes={allPackages}
          isSubscribed={hasAccess}
          profileUsername={profile.username}
          isMobile={isMobile}
        />
        <ProfileReviews reviews={profile.reviews ?? []} />
      </div>
      <div className="md:col-span-1">
        {!isCreator && profile.subscription_price > 0 && (
          <SubscriptionCTA
            username={profile.username}
            creatorId={profile.id}
            subscriptionPrice={profile.subscription_price}
            lifetimeSubscriptionPrice={profile.lifetime_subscription_price}
            onSubscribe={handleSubscribe}
            isSubscribed={isSubscribed}
            subscriptionType={subscriptionType}
            isCancelled={isCancelled}
            onCancelSubscription={onCancelSubscription}
          />
        )}
      </div>
    </div>
  );
};
